import { addClass, removeClass } from '@v4/common/utils/dom';
/**
 * A mixin to handle the toggling of a navigation menu
 */
export const navMenu = {
    data() {
        return {
            isNavOpen: false,
            isSearchBarOpen: false,
        };
    },
    methods: {
        /**
         * Toggle the open / close state
         */
        toggleNavMenu() {
            this.isNavOpen = !this.isNavOpen;
            if (this.isNavOpen) {
                addClass(document.body, 'is-nav-open');
            } else {
                removeClass(document.body, 'is-nav-open');
            }
        },
        toggleSearchBar() {
            this.isSearchBarOpen = !this.isSearchBarOpen;
            this.$el.classList.toggle('is-searchbar-open');
            if (this.isSearchBarOpen) {
                this.isNavOpen = false;
            }
        },
    },
};

/**
 * Generic component notifier.
 *
 * @param {String} msg Message to notify
 * @param {Object} [opts={}] Options
 * @param {String} [opts.type='toast'] Type of the notifier. Options: 'toast' | 'modal' (not yet available)
 * @param {String} [opts.level=null] Level of notifier. Options: 'info' | 'success' | 'error'
 */
export const notifier = {
    methods: {
        notify(msg, opts = {}) {
            const type = opts.type || 'toast';
            const level = opts.level || null;
            const toast = this.$toasted;

            if (type === 'toast' && toast) {
                const hasLevel = toast[level];

                // Available options: https://github.com/shakee93/vue-toasted#api
                toast[hasLevel ? level : 'show'](msg, {
                    duration: 3500,
                    action: {
                        text: '✕',
                        onClick: (e, toast) => toast.goAway(0),
                    },
                    ...opts,
                    type: level,
                });
            }
        },
        notifyInfo(msg, opts = {}) {
            this.notify(msg, {
                ...opts,
                level: 'info',
            });
        },
        notifySuccess(msg, opts = {}) {
            this.notify(msg, {
                ...opts,
                level: 'success',
            });
        },
        notifyError(msg, opts = {}) {
            this.notify(msg, {
                ...opts,
                level: 'error',
            });
        },
    },
};

/**
 * A mixin that implements methods to handle deadlines
 */
export const nextDeadline = {
    methods: {
        isNextDeadlineExpired(nextDeadlineStatus) {
            return String(nextDeadlineStatus).toLowerCase() === 'expired';
        },
        isNextDeadlineExpiring(nextDeadlineStatus) {
            return String(nextDeadlineStatus).toLowerCase() === 'urgent';
        },
        isNextDeadlineExpires(nextDeadlineStatus) {
            return String(nextDeadlineStatus).toLowerCase() === 'normal';
        },
        showExpireAlert(nextDeadlineStatus = null) {
            if (!nextDeadlineStatus) {
                return false;
            }

            return this.isNextDeadlineExpiring(nextDeadlineStatus) || this.isNextDeadlineExpired(nextDeadlineStatus);
        },
        getExpireAlertMessage(nextDeadlineStatus, messages) {
            if (this.isNextDeadlineExpired(nextDeadlineStatus)) {
                return messages.expired;
            }

            if (this.isNextDeadlineExpiring(nextDeadlineStatus)) {
                return messages.expiring;
            }

            if (this.isNextDeadlineExpires(nextDeadlineStatus)) {
                return messages.expires;
            }

            return null;
        },
    },
};

/**
 * A mixins to handle ZIP code validation.
 */
export const zipCodeValidator = {
    methods: {
        /**
         * Return the validation rule for the ZIP code, depending on the locale.
         *
         * @param locale
         * @param basicValidation
         * @returns {string}
         */
        getRule(locale, basicValidation = '') {
            return locale === 'en' ? basicValidation : `${basicValidation ? `${basicValidation}|` : ''}min:5|max:5`;
        },
        /**
         * Return the rule to be used in the template.
         *
         * @param basicValidation
         * @returns {*|string}
         */
        zipCodeValidation(basicValidation = '') {
            return this.getRule(this.$i18n.locale, basicValidation);
        },
    },
};

/**
 * Performing window scroll
 */

export const onWindow = {
    methods: {
        onWindow(eventName, callback) {
            if (typeof eventName !== 'string') {
                throw new TypeError('eventName must be a string');
            }

            if (typeof callback !== 'function') {
                throw new TypeError('callback must be a function');
            }

            let ticking = false;
            let requestId;

            const listener = (e) => {
                if (!ticking) {
                    requestId = window.requestAnimationFrame(() => {
                        callback(e);
                        ticking = false;
                    });
                    ticking = true;
                }
            };

            window.addEventListener(eventName, listener);

            return {
                remove: () => {
                    window.removeEventListener(eventName, listener);
                    window.cancelAnimationFrame(requestId);
                },
            };
        },
        onWindowResize(callback) {
            return this.onWindow('resize', callback);
        },
        onWindowScroll(callback) {
            return this.onWindow('scroll', callback);
        },
    },
};
